import { BigNumber, ethers } from 'ethers'

export const BPS_BASE = 10000
export const MAX_BATCH_LISTINGS = 20
export const PAGE_SIZE = 10

export function shouldBlockMarketLoad({ activeKey, nextKey, hasRuntime }) {
    return activeKey !== nextKey || !hasRuntime
}

const PRESET_SECONDS = {
    '1d': 24 * 60 * 60,
    '7d': 7 * 24 * 60 * 60,
    '30d': 30 * 24 * 60 * 60,
    '90d': 90 * 24 * 60 * 60,
}

export function percentToBps(value) {
    const normalized = String(value).trim()
    if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
        throw new Error('Percentage supports at most two decimal places')
    }
    const [whole, fraction = ''] = normalized.split('.')
    return Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
}

export function chunkListings(listings, size = MAX_BATCH_LISTINGS) {
    if (!Number.isInteger(size) || size < 1 || size > MAX_BATCH_LISTINGS) {
        throw new Error(`Batch size must be between 1 and ${MAX_BATCH_LISTINGS}`)
    }
    const batches = []
    for (let index = 0; index < listings.length; index += size) {
        batches.push(listings.slice(index, index + size))
    }
    return batches
}

export function expiryFromPreset(preset, now = Date.now()) {
    if (preset === 'never') return 0
    const seconds = PRESET_SECONDS[preset]
    if (!seconds) throw new Error(`Unsupported expiry preset: ${preset}`)
    return Math.floor(now / 1000) + seconds
}

export function normalizeListingInput({
    tokenId,
    price,
    decimals,
    expiryPreset,
    now,
}) {
    if (!String(price).trim() || Number(price) <= 0) {
        throw new Error('Listing price must be greater than zero')
    }
    return {
        tokenId: BigNumber.from(tokenId),
        price: ethers.utils.parseUnits(String(price), decimals),
        expiresAt: expiryFromPreset(expiryPreset, now),
    }
}

export function buildFeeBreakdown({
    price,
    projectFeeBps,
    platformFeeBps,
    royaltyBps,
}) {
    const rawPrice = BigNumber.from(price)
    const projectFee = rawPrice.mul(projectFeeBps).div(BPS_BASE)
    const platformFee = rawPrice.mul(platformFeeBps).div(BPS_BASE)
    const royaltyFee = rawPrice.mul(royaltyBps).div(BPS_BASE)
    const totalFees = projectFee.add(platformFee).add(royaltyFee)
    return {
        projectFee,
        platformFee,
        royaltyFee,
        totalFees,
        sellerProceeds: rawPrice.sub(totalFees),
    }
}

export function shortAddress(address, left = 6, right = 4) {
    if (!address) return ''
    return `${address.slice(0, left)}...${address.slice(-right)}`
}

export function uniqueTokenIds(values) {
    const seen = new Set()
    return values.filter((value) => {
        const key = BigNumber.from(value).toString()
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

export function parseRouteLocation(pathname) {
    const segments = pathname.replace(/^\/+/, '').split('/').filter(Boolean)
    const itemIndex = segments.indexOf('item')
    return {
        chain: segments[0] || '',
        marketAddress: segments[1] || '',
        tokenId: itemIndex >= 0 ? segments[itemIndex + 1] || '' : '',
    }
}
